import { useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { signOut } from '../lib/auth';
import { resolveInviteUrl } from '../lib/invite-url';
import type { PracticeContext } from '../lib/types';
import type { Session } from '../lib/supabase';

interface Props {
  session: Session;
  context: PracticeContext;
  children: React.ReactNode;
}

export default function Layout({ session, context, children }: Props) {
  const navigate = useNavigate();
  const accent = context.practice.primaryColor ?? '#22c55e';

  useEffect(() => {
    document.documentElement.style.setProperty('--bb-primary', accent);
    const darker = shade(accent, -12);
    document.documentElement.style.setProperty('--bb-primary-dark', darker);
  }, [accent]);

  async function handleLogout() {
    await signOut();
    navigate('/login', { replace: true });
  }

  const inviteFull = resolveInviteUrl(context.practice.inviteUrl);

  return (
    <>
      <nav className="navbar bg-white border-bottom">
        <div className="container-fluid px-4 flex-wrap gap-2">
          <Link to="/" className="navbar-brand navbar-brand-bb d-flex align-items-center gap-2 me-auto">
            {context.practice.logoUrl ? (
              <img
                src={context.practice.logoUrl}
                alt=""
                height={28}
                className="rounded"
              />
            ) : (
              <i className="bi bi-heart-pulse-fill" />
            )}
            <span className="d-none d-md-inline">{context.practice.displayName}</span>
          </Link>

          <ul className="navbar-nav flex-row flex-wrap gap-1">
              <li className="nav-item">
                <NavLink to="/" end className="nav-link">
                  Painel
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/patients" className="nav-link">
                  Pacientes
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/agenda" className="nav-link">
                  Agenda
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/alerts" className="nav-link">
                  Alertas
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/settings" className="nav-link">
                  Consultório
                </NavLink>
              </li>
            </ul>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <a
              href={inviteFull}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-outline-success"
            >
              <i className="bi bi-link-45deg me-1" />
              Convite /c/{context.practice.slug}
            </a>
            <span className="small text-muted">{session.user.email}</span>
            <button
              type="button"
              onClick={handleLogout}
              className="btn btn-sm btn-outline-secondary"
            >
              <i className="bi bi-box-arrow-right me-1" />
              Sair
            </button>
          </div>
        </div>
      </nav>

      <main className="container-fluid px-4 py-4">{children}</main>
    </>
  );
}

function shade(hex: string, percent: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#16a34a';
  const num = parseInt(h, 16);
  const r = Math.min(255, Math.max(0, ((num >> 16) & 0xff) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + percent));
  const b = Math.min(255, Math.max(0, (num & 0xff) + percent));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
